// A provider subagent's resume prompt arrives verbatim. The main session's rules, copied from
// providers/claude/agent.ts (isClaudeTranscriptNoiseText, normalizeClaudeUserPromptText): the UI cannot import them.
import { asTaskNotificationRow } from "./task-notification-prompt.js";
import type { AgentTimelineItem } from "./types.js";

const INTERRUPT_PLACEHOLDER = /^\[Request interrupted by user(?:[^\]]*)\]$/;
const NO_RESPONSE_PLACEHOLDER = "No response requested.";
const LOCAL_COMMAND_STDOUT = /^\s*<local-command-stdout>[\s\S]*<\/local-command-stdout>\s*$/;
const COMMAND_MESSAGE = /<command-message>([\s\S]*?)<\/command-message>/;
const COMMAND_ARGS = /<command-args>([\s\S]*?)<\/command-args>/;
const COMMAND_NAME = /<command-name>([\s\S]*?)<\/command-name>/;
// Claude's session-change bookkeeping (providers/claude/agent.ts createClaudeSessionChangedNotice;
// the server's agent-reply-blocks.ts keeps it out of chat). The daemon's timeline projection glues
// it onto the next assistant text, e.g. a turn's "[System Error] …" row, so it is cut, not matched whole.
// One or more in a row: a resume that forks and comes back says it twice.
const SESSION_CHANGED_NOTICE =
  /^(?:\s*Claude switched to a new session: \S+ -> (?:[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}|\S+$))+/i;

function isHarnessNoise(text: string): boolean {
  const trimmed = text.trim();
  return (
    INTERRUPT_PLACEHOLDER.test(trimmed) ||
    trimmed === NO_RESPONSE_PLACEHOLDER ||
    LOCAL_COMMAND_STDOUT.test(trimmed)
  );
}

function slash(name: string | undefined): string | undefined {
  if (!name) return undefined;
  return name.startsWith("/") ? name : `/${name}`;
}

function commandPromptText(text: string): string | undefined {
  const command =
    slash(COMMAND_NAME.exec(text)?.[1]?.trim()) ?? slash(COMMAND_MESSAGE.exec(text)?.[1]?.trim());
  if (!command) return undefined;
  const args = COMMAND_ARGS.exec(text)?.[1]?.trim();
  return args ? `${command} ${args}` : command;
}

/** The row an item draws as: unchanged, rewritten, a finished line, or undefined (hidden). */
export function asDrawnPromptRow(item: AgentTimelineItem): AgentTimelineItem | undefined {
  if (item.type === "assistant_message" && SESSION_CHANGED_NOTICE.test(item.text)) {
    const text = item.text.replace(SESSION_CHANGED_NOTICE, "");
    return text.trim() ? { ...item, text } : undefined;
  }
  const row = asTaskNotificationRow(item);
  if (row?.type !== "user_message") return row;
  if (isHarnessNoise(row.text)) return undefined;
  if (!COMMAND_MESSAGE.test(row.text)) return row;
  const text = commandPromptText(row.text.trim());
  return text ? { ...row, text } : undefined;
}
